import { FormEvent, useEffect, useMemo, useState } from 'react';
import { FiCamera, FiCheck, FiLogIn, FiLogOut, FiShield, FiUser } from 'react-icons/fi';
import { useAuthStore } from '../stores/authStore';
import { usePropsStore } from '../stores/propsStore';

const fieldStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '13px 14px', borderRadius: 12,
  border: '1px solid #30363b', background: '#111416', color: '#f4f5f6', fontSize: 15,
};
const lime = '#B7FF00';

function dateLabel(value?: number | null) {
  if (!value) return 'Sem data de expiração';
  return new Date(value).toLocaleDateString('pt-BR');
}

export function AccountScreen() {
  const { token, profile, signIn, signUp, refreshProfile, signOut, checkout, redeemKey, uploadPhoto, profilePhotoUrl } = useAuthStore();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoVersion, setPhotoVersion] = useState(0);

  useEffect(() => {
    if (!token) return;
    refreshProfile().then(() => usePropsStore.getState().fetchAll()).catch(() => setError('Sua sessão expirou. Entre novamente.'));
  }, [token, refreshProfile]);

  useEffect(() => {
    if (!token) return;
    const refresh = () => { if (document.visibilityState === 'visible') refreshProfile().then(() => usePropsStore.getState().fetchAll()).catch(() => undefined); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => { window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, [token, refreshProfile]);

  useEffect(() => {
    if (!token || !profile?.hasProfilePhoto) { setPhotoUrl(''); return; }
    let objectUrl = '';
    let cancelled = false;
    fetch(`${profilePhotoUrl()}?v=${profile.photoUpdatedAt ?? photoVersion}`, {
      headers: { Authorization: `Bearer ${token}` }, cache: 'no-store',
    }).then(response => {
      if (!response.ok) throw new Error('Foto indisponível');
      return response.blob();
    }).then(blob => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(blob);
      setPhotoUrl(objectUrl);
    }).catch(() => setPhotoUrl(''));
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [token, profile?.hasProfilePhoto, profile?.photoUpdatedAt, profilePhotoUrl, photoVersion]);

  const isPremium = profile?.plan === 'PREMIUM' || profile?.isAdmin === true;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setBusy(true); setError(''); setNotice('');
    try {
      const data = new FormData(event.currentTarget);
      if (mode === 'login') {
        await signIn(identifier, password);
        await usePropsStore.getState().fetchAll();
      } else if (mode === 'register') {
        const age = Number(data.get('age'));
        if (!Number.isInteger(age) || age < 18) throw new Error('É necessário ter 18 anos ou mais para criar uma conta.');
        const email = String(data.get('email') || '').trim();
        const result = await signUp({
          email, username: String(data.get('username') || '').trim(), password: String(data.get('password') || ''),
          firstName: String(data.get('firstName') || '').trim(), lastName: String(data.get('lastName') || '').trim(),
          age, sex: String(data.get('sex') || ''),
        });
        if (!result.token) throw new Error(result.error || 'A conta foi criada, mas não foi possível iniciar a sessão. Entre com seu usuário e senha.');
        await usePropsStore.getState().fetchAll();
      }
    } catch (e) { setError((e as Error).message || 'Não foi possível concluir.'); }
    finally { setBusy(false); }
  };

  const choosePlan = async (plan: '1m' | '3m' | '6m') => {
    setBusy(true); setError('');
    try {
      const url = await checkout(plan);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const handlePhoto = async (file?: File) => {
    if (!file) return;
    setBusy(true); setError('');
    try {
      if (!file.type.startsWith('image/')) throw new Error('Escolha uma imagem.');
      const bitmap = await createImageBitmap(file);
      const scale = Math.min(1, 512 / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale)); canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height); bitmap.close();
      let quality = 0.86;
      let blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
      while (blob && blob.size > 370_000 && quality > 0.45) {
        quality -= 0.1; blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
      }
      if (!blob || blob.size > 380_000) throw new Error('A foto ficou muito grande. Escolha outra imagem.');
      await uploadPhoto(blob); setPhotoVersion(Date.now());
    } catch (e) { setError((e as Error).message || 'Não foi possível salvar a foto.'); }
    finally { setBusy(false); }
  };

  const planCards = useMemo(() => [
    { id: '1m' as const, title: '1 mês', price: 'R$ 39,90', detail: 'Acesso por 30 dias' },
    { id: '3m' as const, title: '3 meses', price: 'R$ 89,90', detail: 'Acesso por 90 dias' },
    { id: '6m' as const, title: '6 meses', price: 'R$ 169,90', detail: 'Acesso por 180 dias' },
  ], []);

  return <main style={{ maxWidth: 560, margin: '0 auto', padding: '24px 16px 112px', color: '#f4f5f6' }}>
    <header style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
      <div style={{ width: 44, height: 44, borderRadius: 14, display: 'grid', placeItems: 'center', background: '#B7FF001c', color: lime }}><FiUser size={22} /></div>
      <div><h1 style={{ margin: 0, fontSize: 24 }}>Perfil</h1><p style={{ margin: '4px 0 0', color: '#9ba1a5', fontSize: 13 }}>Sua conta e seu acesso Rush Props</p></div>
    </header>
    {error && <div role="alert" style={{ background: '#4c2020', border: '1px solid #813838', borderRadius: 12, padding: 12, marginBottom: 14, color: '#ffd9d9' }}>{error}</div>}
    {notice && <div role="status" style={{ background: '#1b3020', border: '1px solid #365b37', borderRadius: 12, padding: 12, marginBottom: 14, color: '#d6ffd4' }}>{notice}</div>}

    {!token || !profile ? <section style={{ background: '#111416', border: '1px solid #292e32', borderRadius: 18, padding: 18 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {(['login', 'register'] as const).map(item => <button key={item} onClick={() => { setMode(item); setError(''); setNotice(''); }} style={{ flex: 1, padding: 11, border: '1px solid #30363b', borderRadius: 11, background: mode === item ? '#B7FF001c' : 'transparent', color: mode === item ? lime : '#aab0b4', fontWeight: 700 }}>{item === 'login' ? 'Entrar' : 'Criar conta'}</button>)}
      </div>
      <form onSubmit={submit} style={{ display: 'grid', gap: 11 }}>
        {mode === 'login' && <>
          <input style={fieldStyle} autoComplete="username" placeholder="E-mail ou usuário" value={identifier} onChange={e => setIdentifier(e.target.value)} required />
          <input style={fieldStyle} autoComplete="current-password" type="password" placeholder="Senha" value={password} onChange={e => setPassword(e.target.value)} required />
        </>}
        {mode === 'register' && <>
          <input style={fieldStyle} name="firstName" placeholder="Nome" autoComplete="given-name" required />
          <input style={fieldStyle} name="lastName" placeholder="Sobrenome" autoComplete="family-name" required />
          <input style={fieldStyle} name="username" placeholder="Nome de usuário" autoComplete="username" required />
          <input style={fieldStyle} name="email" type="email" placeholder="E-mail" autoComplete="email" required />
          <input style={fieldStyle} name="age" type="number" min="18" placeholder="Idade (18+)" required />
          <select style={fieldStyle} name="sex" defaultValue="" required><option value="" disabled>Selecione uma opção</option><option value="Feminino">Feminino</option><option value="Masculino">Masculino</option><option value="Outro">Outro</option><option value="Prefiro não informar">Prefiro não informar</option></select>
          <input style={fieldStyle} name="password" type="password" minLength={8} autoComplete="new-password" placeholder="Senha (mínimo 8 caracteres)" required />
        </>}
        <button disabled={busy} type="submit" style={{ padding: 14, border: 0, borderRadius: 12, background: lime, color: '#111', fontWeight: 800, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, opacity: busy ? .6 : 1 }}><FiLogIn />{busy ? 'Aguarde...' : mode === 'login' ? 'Entrar' : 'Criar minha conta'}</button>
      </form>
    </section> : <>
      <section style={{ display: 'flex', alignItems: 'center', gap: 14, background: '#111416', border: '1px solid #292e32', borderRadius: 18, padding: 18, marginBottom: 14 }}>
        <label title="Alterar foto de perfil" style={{ width: 66, height: 66, borderRadius: '50%', overflow: 'hidden', flex: '0 0 auto', display: 'grid', placeItems: 'center', background: '#252b2f', color: lime, cursor: 'pointer', position: 'relative' }}>
          {photoUrl ? <img src={photoUrl} alt="Foto de perfil" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <FiUser size={28} />}
          <span style={{ position: 'absolute', inset: 'auto 0 0', background: '#0009', display: 'grid', placeItems: 'center', height: 22 }}><FiCamera size={13} /></span>
          <input type="file" accept="image/*" aria-label="Alterar foto de perfil" onChange={e => handlePhoto(e.target.files?.[0])} style={{ position: 'absolute', width: 1, height: 1, opacity: 0 }} />
        </label>
        <div style={{ minWidth: 0, flex: 1 }}><div style={{ fontWeight: 800, fontSize: 18, overflowWrap: 'anywhere' }}>{[profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.username || 'Minha conta'}</div><div style={{ color: '#9ba1a5', fontSize: 13, overflowWrap: 'anywhere' }}>{profile.email}</div>{profile.username && <div style={{ color: '#737c81', fontSize: 12 }}>@{profile.username}</div>}</div>
        <button aria-label="Sair da conta" title="Sair" onClick={() => { signOut(); usePropsStore.getState().fetchAll(); setMode('login'); }} style={{ border: 0, background: 'transparent', color: '#9ba1a5', padding: 8 }}><FiLogOut size={19} /></button>
      </section>

      <section style={{ background: '#111416', border: `1px solid ${isPremium ? '#55662b' : '#292e32'}`, borderRadius: 18, padding: 18, marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}><div><div style={{ color: '#9ba1a5', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Seu plano</div><div style={{ fontSize: 20, fontWeight: 800, marginTop: 4 }}>{isPremium ? 'Rush Props Premium' : 'Plano gratuito'}</div></div><div style={{ color: lime, display: 'flex', alignItems: 'center', gap: 5, fontWeight: 700 }}>{isPremium ? <><FiCheck /> Ativo</> : <FiShield size={20} />}</div></div>
        {isPremium && profile.premiumUntil && <p style={{ color: '#9ba1a5', fontSize: 13, margin: '10px 0 0' }}>Acesso até {dateLabel(profile.premiumUntil)}</p>}
        {!isPremium && <>
          <p style={{ color: '#aab0b4', fontSize: 14, lineHeight: 1.5 }}>Desbloqueie todas as picks e análises de NBA e NHL.</p>
          <div style={{ display: 'grid', gap: 9 }}>
            {planCards.map(plan => <button key={plan.id} disabled={busy} onClick={() => choosePlan(plan.id)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, width: '100%', padding: 13, border: '1px solid #343a3e', borderRadius: 12, background: '#181c1f', color: '#f4f5f6', textAlign: 'left', opacity: busy ? .6 : 1 }}><span><b>{plan.title}</b><small style={{ display: 'block', color: '#8d9599', marginTop: 3 }}>{plan.detail}</small></span><b style={{ color: lime }}>{plan.price}</b></button>)}
          </div>
          <form onSubmit={async e => { e.preventDefault(); const form = e.currentTarget; const data = new FormData(form); setBusy(true); setError(''); try { await redeemKey(String(data.get('trialCode') || '').trim()); await usePropsStore.getState().fetchAll(); setNotice('Código resgatado. Seu Premium está ativo!'); form.reset(); } catch (err) { setError((err as Error).message); } finally { setBusy(false); } }} style={{ display: 'flex', gap: 8, marginTop: 14 }}><input name="trialCode" style={{ ...fieldStyle, minWidth: 0 }} placeholder="Código de acesso" required /><button disabled={busy} style={{ border: 0, borderRadius: 11, padding: '0 14px', background: lime, color: '#111', fontWeight: 800 }}>Resgatar</button></form>
        </>}
      </section>
      <div style={{ color: '#737c81', fontSize: 12, padding: '0 4px' }}>Conta criada em {dateLabel(profile.created)}</div>
    </>}
  </main>;
}
